import React from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';

/**
 * Table — v2 inventory table.
 *
 * A thin wrapper around a semantic <table> that applies the v2 visual
 * contract: muted-tinted sticky header, hairline-bordered rows, hover
 * tint, optional click handler per row, density toggle (comfortable
 * by default; compact for power-user dense views per Design Principles
 * §P6).
 *
 * Rendering is column-driven: pass `columns` (array) + `rows` (array of
 * objects). Each column has `key`, `header`, optional `render`, optional
 * `width`, optional `align`, optional `sortable`.
 *
 * Sorting is *controlled* — pass `sortKey` + `sortDir` and a `onSort`
 * callback. The component renders the sort indicator but never mutates
 * the rows.
 *
 * @param {Object} props
 * @param {Array<{
 *   key: string,
 *   header: React.ReactNode,
 *   render?: (row: any, idx: number) => React.ReactNode,
 *   width?: string,
 *   align?: 'left'|'right'|'center',
 *   sortable?: boolean,
 * }>} props.columns
 * @param {Array<any>}        props.rows
 * @param {(row: any, idx: number) => string} [props.rowKey]
 * @param {(row: any, idx: number) => void}   [props.onRowClick]
 * @param {'comfortable'|'compact'}           [props.density='comfortable']
 * @param {string}                            [props.sortKey]
 * @param {'asc'|'desc'}                      [props.sortDir]
 * @param {(key: string, dir: 'asc'|'desc') => void} [props.onSort]
 * @param {React.ReactNode}                   [props.empty]
 *   Element rendered when rows is empty.
 * @param {string}                            [props.ariaLabel='Data table']
 */
export function Table({
  columns,
  rows,
  rowKey,
  onRowClick,
  density = 'comfortable',
  sortKey,
  sortDir,
  onSort,
  empty,
  ariaLabel = 'Data table',
}) {
  const cellPad = density === 'compact' ? 'px-3 py-2' : 'px-4 py-3';
  const headPad = density === 'compact' ? 'px-3 py-2' : 'px-4 py-2.5';

  const handleSort = (col) => {
    if (!col.sortable || !onSort) return;
    const nextDir = sortKey === col.key && sortDir === 'asc' ? 'desc' : 'asc';
    onSort(col.key, nextDir);
  };

  const alignClass = (a) => (a === 'right' ? 'text-right' : a === 'center' ? 'text-center' : 'text-left');

  return (
    <div className="bg-v2-surface border border-v2-border-subtle rounded-v2-md shadow-v2-sm overflow-x-auto">
      <table className="w-full border-collapse" aria-label={ariaLabel}>
        <thead>
          <tr>
            {columns.map((col) => {
              const sorted = sortKey === col.key;
              return (
                <th
                  key={col.key}
                  scope="col"
                  style={col.width ? { width: col.width } : undefined}
                  aria-sort={sorted ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                  className={
                    'bg-v2-muted border-b border-v2-border-subtle ' +
                    'text-[10.5px] font-semibold tracking-wider uppercase text-v2-ink-muted ' +
                    headPad + ' ' + alignClass(col.align)
                  }
                >
                  {col.sortable ? (
                    <button
                      type="button"
                      onClick={() => handleSort(col)}
                      className={
                        'v2-focus-ring inline-flex items-center gap-1 hover:text-v2-ink ' +
                        'transition-colors duration-v2'
                      }
                    >
                      <span>{col.header}</span>
                      {sorted && (
                        sortDir === 'asc'
                          ? <ChevronUp size={11} aria-hidden="true" />
                          : <ChevronDown size={11} aria-hidden="true" />
                      )}
                    </button>
                  ) : (
                    col.header
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {(!rows || rows.length === 0) ? (
            <tr>
              <td colSpan={columns.length} className="p-6 text-center text-v2-ink-muted text-[13px]">
                {empty || 'No data to show.'}
              </td>
            </tr>
          ) : rows.map((row, i) => {
            const isInteractive = typeof onRowClick === 'function';
            return (
              <tr
                key={rowKey ? rowKey(row, i) : i}
                onClick={isInteractive ? () => onRowClick(row, i) : undefined}
                onKeyDown={
                  isInteractive
                    ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRowClick(row, i); } }
                    : undefined
                }
                role={isInteractive ? 'button' : undefined}
                tabIndex={isInteractive ? 0 : undefined}
                className={
                  'border-b border-v2-border-subtle last:border-b-0 ' +
                  (isInteractive
                    ? 'cursor-pointer hover:bg-v2-muted transition-colors duration-v2 v2-focus-ring'
                    : '')
                }
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={
                      'text-[12.5px] text-v2-ink ' + cellPad + ' ' + alignClass(col.align)
                    }
                  >
                    {col.render ? col.render(row, i) : row[col.key]}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default Table;
